import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/shared/form-field';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { formatDate } from '@/utils/format';

export interface FollowUpRow {
  id: string;
  memberId: string;
  memberName: string;
  reason: 'first_time_visit' | 'absence' | 'evangelism' | 'welfare' | 'manual';
  status: 'open' | 'in_progress' | 'completed' | 'dropped';
  dueDate: string | null;
  assignedToMemberId: string | null;
  assignedToMemberName: string | null;
  outcome: string | null;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface FollowUpDialogProps {
  followUp: FollowUpRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFollowUpUpdated?: (followUp: FollowUpRow) => void;
}

export const FollowUpDialog = ({
  followUp,
  open,
  onOpenChange,
  onFollowUpUpdated,
}: FollowUpDialogProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();

  const canAssign = hasPermission(membership, PERMISSIONS.FOLLOWUP_ASSIGN);
  const canComplete = hasPermission(membership, PERMISSIONS.FOLLOWUP_COMPLETE);

  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [outcome, setOutcome] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [dropOpen, setDropOpen] = useState(false);

  useEffect(() => {
    if (!followUp) return;
    setAssigneeId(followUp.assignedToMemberId);
    setDueDate(followUp.dueDate);
    setOutcome(followUp.outcome ?? '');
    setMemberQuery('');
  }, [followUp]);

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'follow-up-assign', memberQuery],
    enabled: open && canAssign,
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '20' });
      if (memberQuery.trim()) params.set('q', memberQuery.trim());
      return api.get<{ data: MemberOption[] }>(`/people/members?${params.toString()}`);
    },
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );

  const selectedAssignee = useMemo(() => {
    const currentId = assigneeId ?? followUp?.assignedToMemberId ?? null;
    if (!currentId) return null;
    const fromOptions = memberOptions.find((option) => option.id === currentId);
    if (fromOptions) return fromOptions;
    if (followUp?.assignedToMemberId === currentId && followUp.assignedToMemberName) {
      return { id: currentId, label: followUp.assignedToMemberName };
    }
    return null;
  }, [assigneeId, followUp, memberOptions]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['people', 'follow-ups'] });
    void queryClient.invalidateQueries({ queryKey: ['people', 'insights'] });
  };

  const onError = (err: unknown) => {
    toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
  };

  const saveDetails = useMutation({
    mutationFn: () =>
      api.patch<FollowUpRow>(`/people/follow-ups/${followUp!.id}`, {
        assignedToMemberId: assigneeId,
        dueDate,
      }),
    onSuccess: (row) => {
      toast.success(t('followUps.saved'));
      invalidate();
      setAssigneeId(row.assignedToMemberId);
      setDueDate(row.dueDate);
    },
    onError,
  });

  const startProgress = useMutation({
    mutationFn: () =>
      api.patch<FollowUpRow>(`/people/follow-ups/${followUp!.id}`, { status: 'in_progress' }),
    onSuccess: (row) => {
      toast.success(t('followUps.saved'));
      invalidate();
      onFollowUpUpdated?.(row);
    },
    onError,
  });

  const complete = useMutation({
    mutationFn: () =>
      api.patch<FollowUpRow>(`/people/follow-ups/${followUp!.id}`, {
        status: 'completed',
        outcome: outcome.trim(),
      }),
    onSuccess: () => {
      toast.success(t('followUps.saved'));
      invalidate();
      onOpenChange(false);
    },
    onError,
  });

  const drop = useMutation({
    mutationFn: () =>
      api.patch<FollowUpRow>(`/people/follow-ups/${followUp!.id}`, {
        status: 'dropped',
        outcome: outcome.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('followUps.saved'));
      invalidate();
      setDropOpen(false);
      onOpenChange(false);
    },
    onError,
  });

  if (!followUp) return null;

  const isOpen = followUp.status === 'open' || followUp.status === 'in_progress';
  const isInProgress = followUp.status === 'in_progress';
  const detailsChanged =
    assigneeId !== followUp.assignedToMemberId || dueDate !== followUp.dueDate;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="md" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('followUps.detail.title', { name: followUp.memberName })}</DialogTitle>
          </DialogHeader>

          <DialogBody className="overflow-visible" contentClassName="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge>{t(`followUps.reason.${followUp.reason}`)}</Badge>
              <Badge variant="neutral">{t(`followUps.status.${followUp.status}`)}</Badge>
            </div>

            <p className="text-body-sm">
              <Link
                to={`/people/${followUp.memberId}`}
                className="font-medium text-accent hover:underline"
              >
                {t('followUps.detail.viewPerson')}
              </Link>
            </p>

            {isOpen && canAssign ? (
              <>
                <FormField label={t('followUps.detail.assignLabel')}>
                  {(field) => (
                    <EntityCombobox
                      id={field.id}
                      value={assigneeId}
                      onChange={setAssigneeId}
                      options={memberOptions}
                      selected={selectedAssignee}
                      onQueryChange={setMemberQuery}
                      loading={members.isFetching}
                      placeholder={t('followUps.detail.assignPlaceholder')}
                      clearLabel={tCommon('action.clearSelection')}
                      popoverPortal="dialog"
                    />
                  )}
                </FormField>

                <FormField label={t('followUps.detail.dueDateLabel')}>
                  {(field) => (
                    <DatePicker
                      id={field.id}
                      value={dueDate}
                      onChange={setDueDate}
                      popoverPortal="dialog"
                    />
                  )}
                </FormField>
              </>
            ) : (
              <dl className="grid gap-3 text-body-sm sm:grid-cols-2">
                <div>
                  <dt className="text-caption text-fg-secondary">
                    {t('followUps.detail.assignLabel')}
                  </dt>
                  <dd>{followUp.assignedToMemberName ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-caption text-fg-secondary">
                    {t('followUps.detail.dueDateLabel')}
                  </dt>
                  <dd>{followUp.dueDate ? formatDate(followUp.dueDate) : '—'}</dd>
                </div>
              </dl>
            )}

            {isInProgress && canComplete ? (
              <FormField label={t('followUps.outcome')}>
                {(field) => (
                  <Textarea
                    id={field.id}
                    value={outcome}
                    onChange={(event) => setOutcome(event.target.value)}
                    placeholder={t('followUps.detail.outcomePlaceholder')}
                    rows={3}
                  />
                )}
              </FormField>
            ) : followUp.outcome ? (
              <div>
                <p className="mb-1 text-caption text-fg-secondary">{t('followUps.outcome')}</p>
                <p className="whitespace-pre-wrap text-body-sm">{followUp.outcome}</p>
              </div>
            ) : null}
          </DialogBody>

          {isOpen ? (
            <DialogFooter className="sm:flex-wrap">
              {canAssign && detailsChanged ? (
                <Button
                  variant="outline"
                  loading={saveDetails.isPending}
                  onClick={() => saveDetails.mutate()}
                >
                  {tCommon('action.save')}
                </Button>
              ) : null}
              {canComplete && followUp.status === 'open' ? (
                <Button
                  variant="outline"
                  loading={startProgress.isPending}
                  onClick={() => startProgress.mutate()}
                >
                  {t('followUps.detail.startProgress')}
                </Button>
              ) : null}
              {canComplete ? (
                <Button variant="destructive" onClick={() => setDropOpen(true)}>
                  {t('followUps.detail.drop')}
                </Button>
              ) : null}
              {canComplete && isInProgress ? (
                <Button
                  loading={complete.isPending}
                  disabled={!outcome.trim()}
                  onClick={() => complete.mutate()}
                >
                  {t('followUps.complete')}
                </Button>
              ) : null}
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={dropOpen}
        onOpenChange={setDropOpen}
        title={t('followUps.detail.dropConfirm.title')}
        description={t('followUps.detail.dropConfirm.description')}
        confirmLabel={t('followUps.detail.dropConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={drop.isPending}
        onConfirm={() => drop.mutate()}
      />
    </>
  );
};
